import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { style } from './edit-identity.style.js';
import { stratum } from '../../../../../constants/stratum.js';

class RefiEditIdentity extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiEditIdentity');
    this.clickTagSent = false;
  }

  static properties = {
    invalidAge: { type: Boolean, reflect: true },
    invalidDate: { type: Boolean, reflect: true },
    dobFocused: { type: Boolean, reflect: true },
    dateOfBirth: { type: String, reflect: true },
    applicant: { type: Object, reflect: true },
  };

  static styles = [style];

  connectedCallback() {
    super.connectedCallback();
    this.dateOfBirth = this.applicant.dateOfBirth;
  }

  render() {
    return html` <div class="checkout-identity-edit">
      <fs-text-field
        class="dob"
        id="checkout-dob-edit"
        autocomplete="bday"
        name="dob"
        type="text"
        pattern="(?:0[1-9]|1[012])/?\\*?\\*?/?(?:19|20)\\d\\d"
        pattern-mismatch-message="Please enter a valid date of birth and use MM/DD/YYYY format"
        value-missing-message="Please enter a valid date of birth and use MM/DD/YYYY format"
        maxLength="10"
        required
        @change="${(ev) => {
          this.dateOfBirth = ev.target.value;
          this.changed('dateOfBirth', ev);
        }}"
        @blur="${(ev) => {
          this.dispatchEvent(new CustomEvent('unsaved-fields'));
          this.dobFocused = false;
          this.invalidAgeCheck();
          const parts = this.dateOfBirth.split('/');
          this.invalidDateCheck(
            ev,
            parseInt(parts[0], 10),
            parseInt(parts[1], 10),
            parseInt(parts[2], 10)
          );
          if (this.invalidDate) {
            stratum.publishTag(
              TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
              {
                replacements: {
                  value:
                    'Please enter a valid date of birth and use MM/DD/YYYY format',
                },
              }
            );
          } else if (this.invalidAge) {
            stratum.publishTag(
              TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
              {
                replacements: {
                  value: 'Applicant must be at least 18 years of age',
                },
              }
            );
          } else {
            stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
              replacements: { value: 'dateOfBirth' },
            });
          }
        }}"
        @focus="${() => {
          this.dobFocused = true;
        }}"
        value="${this.dobFocused
          ? this.dateOfBirth
          : this.maskDateOfBirth(this.dateOfBirth)}"
        error-message="${this.invalidDate || this.invalidAge
          ? this.invalidDate
            ? 'Please enter a valid date of birth and use MM/DD/YYYY format'
            : 'Applicant must be at least 18 years of age'
          : ''}"
      >
        Date of birth
      </fs-text-field>
      <fs-text-field
        id="checkout-last-four-ssn"
        type="text"
        value="***-**-${this.applicant.lastFourSsn}"
        @click="${this.ssnFieldClicked}"
        readonly
        required
        name="ssn-input"
      >
        <slot name="ssn">Social Security Number</slot>
      </fs-text-field>
    </div>`;
  }

  ssnFieldClicked() {
    if (!this.clickTagSent) {
      this.debug.log('SSN field Clicked');
      stratum.publishTag(TagKey.PERSONAL_CHECKOUT_SSN_CLICKED);
      this.clickTagSent = true;
    }
  }

  changed(name, ev) {
    this.applicant[name] = ev.target?.value;
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name,
          isValid: ev.target.validity.valid,
        },
      })
    );
  }

  maskDateOfBirth(dateOfBirth) {
    if (dateOfBirth && dateOfBirth.length === 10) {
      return `${dateOfBirth.substring(0, 3)}**${dateOfBirth.substring(5)}`;
    }

    return dateOfBirth;
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('dateOfBirth')) {
      this.autoFormatDate();
    }
  }

  autoFormatDate() {
    const inputValue = this.dateOfBirth.replace('/[^0-9/]/g', '');
    let inputExcludingExtraSlash = inputValue;
    for (let i = 0; i < inputValue.length; i += 1) {
      if (i !== 2 && i !== 5 && inputValue[i] === '/') {
        inputExcludingExtraSlash =
          inputValue.slice(0, i) + inputValue.slice(i + 1);
      }
    }

    if (
      inputExcludingExtraSlash.length > 2 &&
      inputExcludingExtraSlash[2] !== '/'
    ) {
      inputExcludingExtraSlash = `${inputExcludingExtraSlash.slice(
        0,
        2
      )}/${inputExcludingExtraSlash.slice(2)}`;
    }
    if (
      inputExcludingExtraSlash.length > 5 &&
      inputExcludingExtraSlash[5] !== '/'
    ) {
      inputExcludingExtraSlash = `${inputExcludingExtraSlash.slice(
        0,
        5
      )}/${inputExcludingExtraSlash.slice(5)}`;
    }
    this.dateOfBirth = inputExcludingExtraSlash;
  }

  invalidDateCheck(ev, month, day, year) {
    const validity = !(
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      Number.isNaN(year) ||
      month > 12 ||
      day > 31 ||
      day <= 0 ||
      month <= 0 ||
      (day > 30 && [4, 6, 9, 11].includes(month)) ||
      (year % 4 === 0 && month === 2 && day > 29) ||
      (year % 4 !== 0 && month === 2 && day > 28)
    );
    this.invalidDate = !validity;
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: { name: 'dateOfBirth', isValid: validity },
      })
    );
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: { name: 'invalid-date', isValid: validity },
      })
    );
  }

  invalidAgeCheck() {
    const dobField = this.shadowRoot.getElementById('checkout-dob-edit');
    if (Number.isNaN(new Date(dobField.value)) || dobField.value === '') {
      this.invalidAge = false;
      return;
    }
    this.invalidAge = !this.isAtLeast18YearsOld(dobField.value);
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: 'invalidAge',
          isValid: !this.invalidAge,
        },
      })
    );
  }

  isAtLeast18YearsOld(dob) {
    const currentDate = new Date();
    const eighteenYearsAgo = new Date(
      currentDate.getFullYear() - 18,
      currentDate.getMonth(),
      currentDate.getDate()
    );
    return new Date(dob) <= eighteenYearsAgo;
  }
}

customElements.define('refi-edit-identity', RefiEditIdentity);
